import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HardDriveDownload, Trash2, Upload } from 'lucide-react';
import type {
  CampaignDevice,
  DeviceSummary,
  FirmwareCampaign,
  FirmwareFile,
  Page,
} from '@simpleacs/shared';
import { api } from '../lib/api';
import { useTasks } from '../lib/tasks';
import { formatBytes, formatTimestamp } from '../lib/format';
import { useSession } from '../lib/session';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  PageHeader,
  Pill,
  Spinner,
  StatCard,
  TBody,
  THead,
  TRow,
  Table,
} from '../components/ui';

/**
 * Firmware library, targeted push, and staged campaigns. A campaign pushes
 * one batch and waits for every device in it to inform back on the target
 * version before the next batch goes; one failure pauses everything.
 * Created is never started implicitly.
 */
export function FirmwarePage() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const [notice, setNotice] = useState<string | null>(null);
  const [pushing, setPushing] = useState<FirmwareFile | null>(null);

  const filesQuery = useQuery({
    queryKey: ['firmware'],
    queryFn: () => api.get<Page<FirmwareFile>>('/firmware'),
  });

  const remove = useMutation({
    mutationFn: (name: string) => api.delete(`/firmware/${encodeURIComponent(name)}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['firmware'] }),
  });

  const files = filesQuery.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Firmware"
        description="Images stored on the ACS file server, pushed to devices over TR-069."
        actions={can('settings.manage') ? <UploadButton onDone={setNotice} /> : null}
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Images"
          value={files.length}
          icon={<HardDriveDownload className="size-4" />}
        />
        <StatCard
          label="Total size"
          value={formatBytes(files.reduce((sum, f) => sum + (f.size ?? 0), 0))}
        />
        <StatCard label="Models covered" value={new Set(files.map((f) => f.productClass).filter(Boolean)).size} />
        <CampaignStat />
      </div>

      {notice && (
        <div className="mt-4 rounded-lg border border-info/30 bg-info-soft px-4 py-2.5 text-sm text-info">
          {notice}
        </div>
      )}

      <Card className="mt-5">
        {filesQuery.isLoading ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : filesQuery.error ? (
          <div className="p-5">
            <ErrorNotice error={filesQuery.error} />
          </div>
        ) : files.length === 0 ? (
          <EmptyState
            icon={<HardDriveDownload className="size-8" />}
            title="No firmware images"
            description="Upload an image, tag it with the model it targets, then push it to devices."
          />
        ) : (
          <Table>
            <THead>
              <th className="th">Image</th>
              <th className="th">Model</th>
              <th className="th">Version</th>
              <th className="th">Size</th>
              <th className="th">Uploaded</th>
              <th className="th" />
            </THead>
            <TBody>
              {files.map((file) => (
                <TRow key={file.name}>
                  <td className="td font-mono text-xs">{file.name}</td>
                  <td className="td">
                    {file.productClass ? (
                      <Pill tone="primary">{file.productClass}</Pill>
                    ) : (
                      <Pill tone="warn">untagged</Pill>
                    )}
                  </td>
                  <td className="td text-muted">{file.version ?? '—'}</td>
                  <td className="td tabular-nums text-muted">{formatBytes(file.size)}</td>
                  <td className="td text-muted">{formatTimestamp(file.uploadedAt)}</td>
                  <td className="td">
                    <div className="flex justify-end gap-2">
                      {can('firmware.manage') && (
                        <Button size="sm" variant="primary" onClick={() => setPushing(file)}>
                          Push…
                        </Button>
                      )}
                      {can('settings.manage') && (
                        <Button
                          size="sm"
                          variant="danger"
                          icon={<Trash2 className="size-3.5" />}
                          onClick={() => {
                            if (confirm(`Delete ${file.name}?`)) remove.mutate(file.name);
                          }}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  </td>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      <CampaignsCard files={files} />

      {pushing && <PushDialog file={pushing} onClose={() => setPushing(null)} />}
    </>
  );
}

function CampaignStat() {
  const q = useQuery({
    queryKey: ['firmware', 'campaigns'],
    queryFn: () => api.get<{ items: FirmwareCampaign[] }>('/firmware/campaigns'),
  });
  const running = (q.data?.items ?? []).filter((c) => c.status === 'running').length;
  const paused = (q.data?.items ?? []).filter((c) => c.status === 'paused').length;
  return (
    <StatCard
      label="Campaigns"
      value={q.data ? q.data.items.length : '—'}
      tone={paused > 0 ? 'warn' : running > 0 ? 'info' : 'default'}
      hint={paused > 0 ? `${paused} paused on failure` : running > 0 ? `${running} running` : undefined}
    />
  );
}

const CAMPAIGN_TONE: Record<string, 'neutral' | 'info' | 'warn' | 'success' | 'danger'> = {
  draft: 'neutral',
  running: 'info',
  paused: 'warn',
  completed: 'success',
  cancelled: 'neutral',
};

/**
 * Staged rollouts. Creating one takes an image from the library and SNAPSHOTS
 * the eligible TR-069 devices — created is not started, the first batch is
 * the canary, and one failure pauses everything until a human looks.
 */
function CampaignsCard({ files }: { files: FirmwareFile[] }) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The campaign whose per-device rows are unfolded; one at a time. */
  const [openId, setOpenId] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['firmware', 'campaigns'],
    queryFn: () => api.get<{ items: FirmwareCampaign[] }>('/firmware/campaigns'),
    refetchInterval: 30_000,
  });

  const act = useMutation({
    mutationFn: (input: { id: string; action: 'start' | 'pause' | 'cancel'; retryFailed?: boolean }) =>
      api.post(
        `/firmware/campaigns/${encodeURIComponent(input.id)}/${input.action}`,
        input.retryFailed ? { retryFailed: true } : undefined,
      ),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['firmware', 'campaigns'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not update the campaign'),
  });

  const items = q.data?.items ?? [];

  return (
    <Card
      className="mt-5"
      title="Campaigns — staged rollout, pause on failure"
      action={
        can('firmware.manage') ? (
          <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
            New campaign
          </Button>
        ) : undefined
      }
    >
      {error && <p className="px-5 pt-3 text-sm text-danger">{error}</p>}
      {items.length === 0 ? (
        <EmptyState
          title="No campaigns"
          description="A campaign pushes one batch, waits for every device in it to come back on the new version, then pushes the next. One failure pauses everything."
        />
      ) : (
        <div className="divide-y divide-line">
          {items.map((c) => {
            const done = c.counts.confirmed;
            const pct = c.counts.total === 0 ? 0 : Math.round((done / c.counts.total) * 100);
            return (
              <div key={c.id} className="px-5 py-3.5">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm font-medium text-ink">{c.name}</span>
                  <Pill state tone={CAMPAIGN_TONE[c.status] ?? 'neutral'}>{c.status}</Pill>
                  <span className="text-xs text-faint">
                    {c.productClass} → {c.targetVersion} · batch {c.batchSize}
                    {c.pauseOnFailure ? ' · pauses on failure' : ''}
                  </span>
                  <span className="ml-auto flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setOpenId(openId === c.id ? null : c.id)}>
                      {openId === c.id ? 'Hide devices' : 'Devices'}
                    </Button>
                    {can('firmware.manage') && (c.status === 'draft' || c.status === 'paused') && (
                      <Button size="sm" variant="primary" loading={act.isPending && act.variables?.id === c.id}
                        onClick={() => act.mutate({ id: c.id, action: 'start' })}>
                        {c.status === 'paused' ? 'Resume' : 'Start'}
                      </Button>
                    )}
                    {can('firmware.manage') && c.status === 'paused' && c.counts.failed > 0 && (
                      <Button size="sm" variant="secondary" loading={act.isPending && act.variables?.id === c.id}
                        onClick={() => {
                          if (confirm(`Push the image again to the ${c.counts.failed} failed device(s) of "${c.name}" once it resumes?`))
                            act.mutate({ id: c.id, action: 'start', retryFailed: true });
                        }}>
                        Resume, retry failed
                      </Button>
                    )}
                    {can('firmware.manage') && c.status === 'running' && (
                      <Button size="sm" variant="ghost" onClick={() => act.mutate({ id: c.id, action: 'pause' })}>
                        Pause
                      </Button>
                    )}
                    {can('firmware.manage') && ['draft', 'running', 'paused'].includes(c.status) && (
                      <Button size="sm" variant="danger" onClick={() => {
                        if (confirm(`Cancel "${c.name}"? Devices already flashed stay on the new firmware.`))
                          act.mutate({ id: c.id, action: 'cancel' });
                      }}>
                        Cancel
                      </Button>
                    )}
                  </span>
                </div>
                <div className="mt-2 flex items-center gap-3">
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface-2">
                    <div className="h-full rounded-full bg-success" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs tabular-nums text-muted">
                    {done}/{c.counts.total} confirmed
                    {c.counts.pushed > 0 ? ` · ${c.counts.pushed} flashing` : ''}
                    {c.counts.failed > 0 ? ` · ${c.counts.failed} failed` : ''}
                  </span>
                </div>
                {openId === c.id && <CampaignDevices campaign={c} />}
              </div>
            );
          })}
        </div>
      )}
      {creating && <CreateCampaignDialog files={files} onClose={() => setCreating(false)} />}
    </Card>
  );
}

/** "v2.0.0" stays as it is; a bare build number gets the v the operator reads. */
function versionLabel(v: string | null): string {
  if (!v) return '—';
  return /^v/i.test(v) ? v : `v${v}`;
}

/**
 * One member's line. Confirmed says HOW: by the version it informed, or —
 * for an image that leaves the reported version alone — by a reboot after
 * the push, an inform after that boot, and no fault since.
 */
function memberLine(d: CampaignDevice, targetVersion: string): string {
  switch (d.status) {
    case 'confirmed':
      return d.confirmedBy === 'reboot'
        ? `confirmed · rebooted, version unchanged (${versionLabel(d.fromVersion)})`
        : `confirmed · ${versionLabel(targetVersion)}`;
    case 'failed':
      return `failed · ${d.error ?? 'no reason recorded'}`;
    case 'pushed':
      return d.pushedAt ? `flashing · pushed ${formatTimestamp(d.pushedAt)}` : 'flashing';
    default:
      return d.status;
  }
}

const MEMBER_TONE: Record<CampaignDevice['status'], 'neutral' | 'info' | 'warn' | 'success' | 'danger'> = {
  pending: 'neutral',
  pushed: 'info',
  confirmed: 'success',
  failed: 'danger',
  skipped: 'neutral',
};

function CampaignDevices({ campaign }: { campaign: FirmwareCampaign }) {
  const q = useQuery({
    queryKey: ['firmware', 'campaigns', campaign.id],
    queryFn: () =>
      api.get<{ campaign: FirmwareCampaign; devices: CampaignDevice[] }>(
        `/firmware/campaigns/${encodeURIComponent(campaign.id)}`,
      ),
    refetchInterval: campaign.status === 'running' ? 15_000 : false,
  });
  if (q.isLoading) return <Spinner className="mt-3" />;
  if (q.error) {
    return (
      <div className="mt-3">
        <ErrorNotice error={q.error} />
      </div>
    );
  }
  const rows = q.data?.devices ?? [];
  if (rows.length === 0) return <p className="mt-3 text-xs text-faint">No devices in this campaign.</p>;
  return (
    <ul className="mt-3 divide-y divide-line rounded-lg border border-line text-xs">
      {rows.map((d) => (
        <li key={d.deviceId} className="flex flex-wrap items-center gap-2 px-3 py-2">
          <span className="font-mono text-ink">{d.serial ?? d.deviceId}</span>
          <span className="text-faint">
            {versionLabel(d.fromVersion)} → {versionLabel(campaign.targetVersion)}
          </span>
          <Pill state tone={MEMBER_TONE[d.status]}>{d.status}</Pill>
          <span className="text-muted">{memberLine(d, campaign.targetVersion)}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Create closes the dialog at once and the snapshot runs in the tasks tray,
 * like Push above; the campaign list refreshes when it lands and the toast
 * says how many devices were captured.
 */
function CreateCampaignDialog({ files, onClose }: { files: FirmwareFile[]; onClose: () => void }) {
  const tasks = useTasks();
  const tagged = files.filter((f) => f.productClass && f.version);
  const [form, setForm] = useState({
    fileName: tagged[0]?.name ?? '',
    name: '',
    batchSize: '1',
    pauseOnFailure: true,
  });
  const file = tagged.find((f) => f.name === form.fileName) ?? null;

  const create = () => {
    // Snapshotted: the dialog is gone by the time the write settles.
    const body = {
      name: form.name.trim() || `${file?.productClass} → ${file?.version}`,
      fileName: form.fileName,
      productClass: file?.productClass,
      targetVersion: file?.version,
      batchSize: Number(form.batchSize) || 1,
      pauseOnFailure: form.pauseOnFailure,
    };
    onClose();
    tasks.start<{ targets: number }>({
      label: `Create campaign ${body.name}`,
      run: () => api.post<{ targets: number }>('/firmware/campaigns', body),
      done: (r) => `Campaign ${body.name} created with ${r.targets} device(s) — not started.`,
      invalidate: [['firmware', 'campaigns']],
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <form
        className="relative w-full max-w-lg rounded-xl border border-line bg-surface shadow-2xl"
        onSubmit={(e) => {
          e.preventDefault();
          create();
        }}
      >
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">New campaign</h2>
          <p className="mt-0.5 text-xs text-muted">
            Snapshots the eligible TR-069 devices now. Created is not started — starting is a
            separate, deliberate step. An image that does not change the version the CPE
            reports confirms by reboot without fault: the device rebooted after the push,
            informed after that boot, and the ACS holds no fault for it since.
          </p>
        </header>
        <div className="grid gap-4 p-5">
          {tagged.length === 0 ? (
            <p className="text-sm text-warn">
              No image in the library carries both a model and a version — upload one with its
              metadata first.
            </p>
          ) : (
            <>
              <Field label="Image (model and target version come from its tags)">
                <select
                  className="input"
                  value={form.fileName}
                  onChange={(e) => setForm({ ...form, fileName: e.target.value })}
                >
                  {tagged.map((f) => (
                    <option key={f.name} value={f.name}>
                      {f.name} — {f.productClass} → {f.version}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Name (optional)">
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={file ? `${file.productClass} → ${file.version}` : ''}
                />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Batch size — 1 makes the first device the canary">
                  <Input
                    type="number"
                    min={1}
                    max={50}
                    value={form.batchSize}
                    onChange={(e) => setForm({ ...form, batchSize: e.target.value })}
                  />
                </Field>
                <label className="flex cursor-pointer items-center gap-2 pt-6 text-sm text-ink">
                  <input
                    type="checkbox"
                    className="size-4 accent-primary"
                    checked={form.pauseOnFailure}
                    onChange={(e) => setForm({ ...form, pauseOnFailure: e.target.checked })}
                  />
                  Pause everything on the first failure
                </label>
              </div>
            </>
          )}
        </div>
        <footer className="flex justify-end gap-2 border-t border-line px-5 py-3.5">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={tagged.length === 0}>
            Create
          </Button>
        </footer>
      </form>
    </div>
  );
}

function UploadButton({ onDone }: { onDone: (message: string) => void }) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);

  async function onPick(file: File) {
    const productClass = prompt('Product class this image targets (e.g. F670L):') ?? '';
    const version = prompt('Firmware version (e.g. V9.0.11P2N2):') ?? '';
    setBusy(true);
    try {
      const query = new URLSearchParams();
      if (productClass.trim()) query.set('productClass', productClass.trim());
      if (version.trim()) query.set('version', version.trim());
      const res = await fetch(
        `/api/firmware/${encodeURIComponent(file.name)}?${query.toString()}`,
        {
          method: 'PUT',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/octet-stream' },
          body: file,
        },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Upload failed (${res.status})`);
      }
      onDone(`Uploaded ${file.name}`);
      await queryClient.invalidateQueries({ queryKey: ['firmware'] });
    } catch (err) {
      onDone(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void onPick(file);
          e.target.value = '';
        }}
      />
      <Button
        variant="primary"
        icon={<Upload className="size-4" />}
        loading={busy}
        onClick={() => inputRef.current?.click()}
      >
        Upload image
      </Button>
    </>
  );
}

/** Pick targets among devices whose product class matches the image tag. */
function PushDialog({
  file,
  onClose,
}: {
  file: FirmwareFile;
  onClose: () => void;
}) {
  const tasks = useTasks();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  const devicesQuery = useQuery({
    queryKey: ['devices', 'for-firmware', file.productClass],
    queryFn: () => api.get<Page<DeviceSummary>>('/devices', { limit: 500 }),
  });

  const candidates = (devicesQuery.data?.items ?? []).filter((d) => {
    if (file.productClass && d.productClass !== file.productClass) return false;
    const term = search.trim().toLowerCase();
    if (!term) return true;
    return [d.alias, d.serial, d.model].filter(Boolean).some((v) => String(v).toLowerCase().includes(term));
  });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-xl border border-line bg-surface shadow-2xl">
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">Push {file.name}</h2>
          <p className="mt-0.5 text-xs text-muted">
            {file.productClass
              ? `Offered only to ${file.productClass} devices.`
              : 'Image is untagged — every device is offered. Tag images with a product class to prevent cross-flashing.'}{' '}
            Devices flash and reboot on their own schedule.
          </p>
        </header>

        <div className="space-y-3 p-5">
          <Field label="Filter">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, serial…" />
          </Field>

          <div className="max-h-72 overflow-y-auto rounded-lg border border-line">
            {devicesQuery.isLoading ? (
              <div className="flex justify-center p-8">
                <Spinner />
              </div>
            ) : candidates.length === 0 ? (
              <p className="p-4 text-sm text-faint">No matching devices.</p>
            ) : (
              candidates.map((device) => (
                <label
                  key={device.id}
                  className="flex cursor-pointer items-center gap-3 border-b border-line px-4 py-2 last:border-0 hover:bg-surface-2"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(device.id)}
                    onChange={() =>
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (next.has(device.id)) next.delete(device.id);
                        else next.add(device.id);
                        return next;
                      })
                    }
                  />
                  <span className="min-w-0 flex-1 truncate text-sm text-ink">
                    {device.alias ?? device.serial}
                  </span>
                  <span className="font-mono text-xs text-faint">{device.softwareVersion ?? '—'}</span>
                  <Pill state tone={device.status === 'online' ? 'success' : 'neutral'} dot>
                    {device.status}
                  </Pill>
                </label>
              ))
            )}
          </div>
        </div>

        <footer className="flex items-center justify-between border-t border-line px-5 py-3">
          <span className="text-xs text-muted">{selected.size} selected</span>
          <div className="flex gap-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="primary"
              disabled={selected.size === 0}
              onClick={() => {
                if (
                  !confirm(
                    `Flash ${selected.size} device(s) with ${file.name}? Each will reboot when done.`,
                  )
                ) {
                  return;
                }
                onClose();
                tasks.start({
                  label: `Push ${file.name} to ${selected.size} device(s)`,
                  run: async () => {
                    let delivered = 0;
                    for (const id of selected) {
                      const result = await api.post<{ delivered: boolean }>(
                        `/firmware/push/${encodeURIComponent(id)}`,
                        { fileName: file.name },
                      );
                      if (result.delivered) delivered += 1;
                    }
                    return delivered;
                  },
                  done: (delivered) =>
                    `${selected.size} pushed, ${delivered} accepted immediately, the rest queued`,
                  invalidate: [['firmware'], ['devices']],
                });
              }}
            >
              Push firmware
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}
